import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './Projects.module.css';
import { ReactComponent as EditIcon } from '../../../assets/icons/PencilIcon.svg';
import { ReactComponent as DragIcon } from '../../../assets/icons/DragIcon.svg';
import { projectsApi } from '../../../api/projects';
import { Project } from '../../../types/api.types';

export const AdminProjects: React.FC = () => {
    const navigate = useNavigate();
    const [filterStatus, setFilterStatus] = useState<string>('published');
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const data = await projectsApi.getAllAdmin();
            setProjects(data);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const filteredProjects = filterStatus === 'all'
        ? projects
        : projects.filter(p => p.status === filterStatus);

    const sortedProjects = [...filteredProjects].sort((a, b) => {
        if (a.columnNumber !== b.columnNumber) {
            return a.columnNumber - b.columnNumber;
        }
        return a.sortOrder - b.sortOrder;
    });

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = async (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        setDragOverIndex(index);

        const newProjects = [...projects];
        const [draggedItem] = newProjects.splice(draggedIndex, 1);
        newProjects.splice(index, 0, draggedItem);

        const columnCounts = [2, 3, 2];
        let colIndex = 0;
        let orderInColumn = 1;

        newProjects.forEach((project, idx) => {
            let sum = 0;
            let found = false;
            for (let i = 0; i < columnCounts.length; i++) {
                sum += columnCounts[i];
                if (idx < sum) {
                    colIndex = i + 1;
                    orderInColumn = idx - (sum - columnCounts[i]) + 1;
                    found = true;
                    break;
                }
            }
            if (!found) {
                colIndex = columnCounts.length;
                orderInColumn = idx - (columnCounts.reduce((a, b) => a + b, 0) - columnCounts[columnCounts.length - 1]) + 1;
            }
            project.columnNumber = colIndex;
            project.sortOrder = orderInColumn;
        });

        setProjects(newProjects);
        setDraggedIndex(index);

        try {
            const updatePromises = newProjects.map(project =>
                projectsApi.update(project.id, {
                    columnNumber: project.columnNumber,
                    sortOrder: project.sortOrder,
                })
            );
            await Promise.all(updatePromises);

        } catch (error) {
            loadProjects();
        }
    };
    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'published': return 'Опубликовано';
            case 'draft': return 'Черновик';
            case 'archived': return 'Архив';
            default: return status;
        }
    };

    const getStatusClass = (status: string) => {
        switch (status) {
            case 'published': return s.statusPublished;
            case 'draft': return s.statusDraft;
            case 'archived': return s.statusArchived;
            default: return '';
        }
    };

    const handleAddProject = (columnIndex: number) => {
        navigate(`/admin/projects/new?column=${columnIndex + 1}`);
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    const columnCounts = [2, 3, 2];
    const columns = columnCounts.map((count, colIndex) => {
        const columnNumber = colIndex + 1;
        const columnProjects = sortedProjects.filter(p => p.columnNumber === columnNumber);

        return (
            <div key={colIndex} className={s.column}>
                {columnProjects.map((project, index) => {
                    const globalIndex = sortedProjects.indexOf(project);
                    return (
                        <div
                            key={project.id}
                            className={`${s.projectCard} ${
                                draggedIndex === globalIndex ? s.dragging : ''
                            } ${
                                dragOverIndex === globalIndex && draggedIndex !== globalIndex ? s.dragOver : ''
                            }`}
                            draggable
                            onDragStart={() => handleDragStart(globalIndex)}
                            onDragOver={(e) => handleDragOver(e, globalIndex)}
                            onDragLeave={handleDragLeave}
                            onDragEnd={handleDragEnd}
                        >
                            <div className={s.cardHeader}>
                                <div className={s.dragHandle}>
                                    <DragIcon className={s.dragIcon} />
                                </div>
                                <div className={s.cardActions}>
                                    <span className={`${s.statusBadge} ${getStatusClass(project.status)}`}>
                                        {getStatusLabel(project.status)}
                                    </span>
                                    <button
                                        className={s.editBtn}
                                        onClick={() => navigate(`/admin/projects/${project.id}`)}
                                    >
                                        <EditIcon className={s.editIcon} />
                                    </button>
                                </div>
                            </div>
                            <h3 className={s.projectTitle}>{project.title}</h3>
                            {project.iconUrl && (
                                <img src={project.iconUrl} alt={project.title} className={s.projectImage} />
                            )}
                        </div>
                    );
                })}
                <div className={s.addCard} onClick={() => handleAddProject(colIndex)}>
                    <button className={s.addBtn}>+</button>
                    <span className={s.addText}>Добавить направление</span>
                </div>
            </div>
        );
    });

    return (
        <div className={s.projects}>
            <div className={s.projectsWrapper}>
                <div className={s.filterBar}>
                    <button
                        className={`${s.filterBtn} ${filterStatus === 'published' ? s.filterActive : ''}`}
                        onClick={() => setFilterStatus('published')}
                    >
                        Опубликовано
                    </button>
                    <button
                        className={`${s.filterBtn} ${filterStatus === 'draft' ? s.filterActive : ''}`}
                        onClick={() => setFilterStatus('draft')}
                    >
                        Черновики
                    </button>
                    <button
                        className={`${s.filterBtn} ${filterStatus === 'archived' ? s.filterActive : ''}`}
                        onClick={() => setFilterStatus('archived')}
                    >
                        Архив
                    </button>
                </div>
                <div className={s.projectsGrid}>
                    {columns}
                </div>
            </div>
        </div>
    );
};